import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { audiencesApi } from '../../../api/audiences';
import { flowsApi } from '../../../api/flows';
import Modal from '../../../components/base/Modal';
import ModalFooter from '../../../components/base/Modal/ModalFooter';
import ModalTitle from '../../../components/base/Modal/ModalTitle';
import styles from '../Contacts.module.scss';
import { useContactsParams } from '../useContactsParams';
import Audiences from './Audiences';
import BuyingPower from './BuyingPower';
import { REACH_METHODS, SOURCES } from './data';
import Flows from './Flows';
import PortfolioValue from './PortfolioValue';
import ReachMethods from './ReachMethods';
import Sources from './Sources';

const FilterModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const { params, updateParam } = useContactsParams();
  const [isLoading, setIsLoading] = useState(true);
  const [reachMethods, setReachMethods] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [flows, setFlows] = useState([]);
  const [portfolioValue, setPortfolioValue] = useState('');
  const [buyingPower, setBuyingPower] = useState('');
  const [sources, setSources] = useState([]);

  useEffect(() => {
    const loadInitialData = async () => {
      let filters = {};

      if (params.filters) {
        try {
          filters = JSON.parse(params.filters);
        } catch (e) {
          console.warn('Invalid filters JSON:', params.filters);
        }
      }

      if (filters.reach_methods) {
        setReachMethods(
          filters.reach_methods
            .map((value) =>
              REACH_METHODS.find((method) => method.value === value)
            )
            .filter(Boolean)
        );
      }

      const promises = [];

      const segmentIds = filters.audiences?.segments || [];
      const listIds = filters.audiences?.lists || [];
      const flowIds = filters.flows || [];

      if (segmentIds.length > 0) {
        const segmentsPromise = dispatch(
          audiencesApi.endpoints.getSegmentsByIds.initiate({
            ids: segmentIds,
          })
        )
          .unwrap()
          .then((data) => ({ type: 'segments', data }));
        promises.push(segmentsPromise);
      }

      if (listIds.length > 0) {
        const listsPromise = dispatch(
          audiencesApi.endpoints.getListsByIds.initiate({ ids: listIds })
        )
          .unwrap()
          .then((data) => ({ type: 'lists', data }));
        promises.push(listsPromise);
      }

      if (flowIds.length > 0) {
        const flowsPromise = dispatch(
          flowsApi.endpoints.getFlowsByIds.initiate({
            ids: flowIds,
          })
        )
          .unwrap()
          .then((data) => ({ type: 'flows', data }));
        promises.push(flowsPromise);
      }

      try {
        const results = await Promise.all(promises);

        let finalAudiences = [];
        let finalFlows = [];
        results.forEach((result) => {
          if (result.type === 'segments') {
            const formattedSegments = result.data.map((item) => ({
              value: item.id,
              label: item.name,
              type: 'segment',
            }));
            finalAudiences = [...finalAudiences, ...formattedSegments];
          } else if (result.type === 'lists') {
            const formattedLists = result.data.map((item) => ({
              value: item.id,
              label: item.name,
              type: 'list',
            }));
            finalAudiences = [...finalAudiences, ...formattedLists];
          } else if (result.type === 'flows') {
            const formattedLists = result.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            finalFlows = formattedLists;
          }
        });

        setAudiences(finalAudiences);
        setFlows(finalFlows);
      } catch (error) {
        setAudiences([]);
        setFlows([]);
      }

      if (filters.min_portfolio_value) {
        setPortfolioValue(filters.min_portfolio_value);
      }

      if (filters.min_buying_power) {
        setBuyingPower(filters.min_buying_power);
      }

      if (filters.sources) {
        setSources(
          filters.sources
            .map((value) => SOURCES.find((source) => source.value === value))
            .filter(Boolean)
        );
      }

      setIsLoading(false);
    };

    loadInitialData();
  }, []);

  const handleReset = () => {
    setReachMethods([]);
    setAudiences([]);
    setFlows([]);
    setPortfolioValue('');
    setBuyingPower('');
    setSources([]);
  };

  const handleConfirm = () => {
    const filters = {};

    if (reachMethods.length > 0) {
      filters.reach_methods = reachMethods.map((item) => item.value);
    }

    if (audiences.length > 0) {
      filters.audiences = {};
      const segments = audiences
        .filter((audience) => audience.type === 'segment')
        .map((audience) => audience.value);
      if (segments.length > 0) {
        filters.audiences.segments = segments;
      }
      const lists = audiences
        .filter((audience) => audience.type === 'list')
        .map((audience) => audience.value);
      if (lists.length > 0) {
        filters.audiences.lists = lists;
      }
    }

    if (flows.length > 0) {
      filters.flows = flows.map((flow) => flow.value);
    }

    if (portfolioValue) {
      filters.min_portfolio_value = Number(portfolioValue);
    }

    if (buyingPower) {
      filters.min_buying_power = Number(buyingPower);
    }

    if (sources.length > 0) {
      filters.sources = sources.map((item) => item.value);
    }

    if (Object.keys(filters).length > 0) {
      updateParam('filters', JSON.stringify(filters));
    } else {
      updateParam('filters');
    }

    closeModal();
  };

  return (
    <Modal onCancel={closeModal}>
      <ModalTitle title="Filters" />

      <div className={styles.filter_container}>
        <ReachMethods
          reachMethods={reachMethods}
          setReachMethods={setReachMethods}
          isLoading={isLoading}
        />
        <Audiences
          audiences={audiences}
          setAudiences={setAudiences}
          isLoading={isLoading}
        />
        <Flows flows={flows} setFlows={setFlows} isLoading={isLoading} />
        <PortfolioValue
          portfolioValue={portfolioValue}
          setPortfolioValue={setPortfolioValue}
        />
        <BuyingPower
          buyingPower={buyingPower}
          setBuyingPower={setBuyingPower}
        />
        <Sources sources={sources} setSources={setSources} />
      </div>

      <ModalFooter>
        <button
          type="button"
          className="outline-button border border-1"
          onClick={handleReset}
        >
          Reset
        </button>
        <button
          type="button"
          className="regular-button"
          onClick={handleConfirm}
        >
          Apply Filters
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default FilterModal;
